import React from 'react';
import { motion } from 'framer-motion';
export interface ReceiptItem {
  name: string;
  price: number;
  quantity?: number;
}
export interface ReceiptVisualProps {
  items: ReceiptItem[];
  storeName?: string;
  showTotal?: boolean;
  totalBudget?: number;
  label?: string;
  mode?: 'stimulus' | 'support';
}
function formatEuro(cents: number): string {
  const euros = cents / 100;
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(euros);
}
function formatEuroFromEuros(euros: number): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(euros);
}
/** Detect whether prices are in cents (>10 and integer) or euros */
function isCents(items: ReceiptItem[]): boolean {
  return items.every(i => Number.isInteger(i.price) && Math.abs(i.price) >= 10);
}
export function ReceiptVisual({
  items,
  storeName,
  showTotal = true,
  totalBudget,
  label,
  mode = 'support'
}: ReceiptVisualProps) {
  const validItems = (items || []).filter(i => i.name && typeof i.price === 'number' && Number.isFinite(i.price));
  if (validItems.length === 0) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-9780">
        <p className="text-red-600 text-sm font-medium" data-id="element-9781">
          Geen producten op de kassabon
        </p>
      </div>;
  }
  const useCents = isCents(validItems);
  const fmt = useCents ? formatEuro : formatEuroFromEuros;
  const lineItems = validItems.map(item => {
    const qty = item.quantity ?? 1;
    const lineTotal = item.price * qty;
    return {
      ...item,
      quantity: qty,
      lineTotal
    };
  });
  const subtotal = lineItems.reduce((sum, i) => sum + i.lineTotal, 0);
  const budgetVal = totalBudget ?? 0;
  const change = budgetVal > 0 ? budgetVal - subtotal : 0;
  // ─── Stimulus mode: compact receipt ──────────────────────────────────
  if (mode === 'stimulus') {
    return <div className="bg-amber-50/80 rounded-xl border border-amber-200 overflow-hidden max-w-xs mx-auto" data-id="element-9782">
        {/* Header */}
        <div className="bg-amber-100 px-4 py-2.5 border-b border-amber-200 text-center" data-id="element-9783">
          <span className="text-xs font-bold text-amber-900 tracking-wide uppercase" data-id="element-9784">
            {storeName || '🧾 Kassabon'}
          </span>
        </div>

        {/* Items */}
        <div className="px-4 py-3 space-y-1.5" data-id="element-9785">
          {lineItems.map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -8
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: i * 0.06,
          duration: 0.25
        }} className="flex items-baseline justify-between gap-2 text-sm" data-id="element-9786">
              <div className="flex items-baseline gap-1.5 min-w-0" data-id="element-9787">
                <span className="text-amber-800 truncate" data-id="element-9788">{item.name}</span>
                {item.quantity > 1 && <span className="text-amber-500 text-xs flex-shrink-0" data-id="element-9789">
                    ×{item.quantity}
                  </span>}
              </div>
              <span className="text-amber-900 font-mono text-xs flex-shrink-0 tabular-nums" data-id="element-9790">
                {fmt(item.lineTotal)}
              </span>
            </motion.div>)}
        </div>

        {/* Total */}
        {showTotal && <div className="border-t border-dashed border-amber-300 px-4 py-2.5 flex justify-between items-baseline" data-id="element-9791">
            <span className="text-sm font-bold text-amber-900" data-id="element-9792">Totaal</span>
            <span className="text-sm font-bold font-mono text-amber-900 tabular-nums" data-id="element-9793">
              {fmt(subtotal)}
            </span>
          </div>}

        {label && <div className="px-4 pb-2.5" data-id="element-9794">
            <p className="text-xs text-amber-600 text-center" data-id="element-9795">{label}</p>
          </div>}
      </div>;
  }
  // ─── Support mode: educational receipt with breakdown ────────────────
  return <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200 shadow-sm overflow-hidden max-w-sm mx-auto" data-id="element-9796">
      {/* Store header */}
      <div className="bg-amber-100 px-5 py-3 border-b border-amber-200 text-center" data-id="element-9797">
        <div className="text-lg font-bold text-amber-900" data-id="element-9798">
          {storeName || '🧾 Kassabon'}
        </div>
        {label && <p className="text-xs text-amber-600 mt-0.5" data-id="element-9799">{label}</p>}
      </div>

      {/* Receipt body */}
      <div className="px-5 py-4" data-id="element-9800">
        {/* Line items */}
        <div className="space-y-2 mb-4" data-id="element-9801">
          {lineItems.map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.08,
          duration: 0.3
        }} className="flex items-center justify-between gap-3" data-id="element-9802">
              <div className="flex items-center gap-2 min-w-0" data-id="element-9803">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" data-id="element-9804" />
                <span className="text-sm text-amber-900 truncate" data-id="element-9805">
                  {item.name}
                </span>
                {item.quantity > 1 && <span className="text-xs text-amber-500 flex-shrink-0" data-id="element-9806">
                    ×{item.quantity}
                  </span>}
              </div>
              <div className="flex items-baseline gap-2 flex-shrink-0" data-id="element-9807">
                {item.quantity > 1 && <span className="text-xs text-amber-400" data-id="element-9808">
                    {fmt(item.price)}/st
                  </span>}
                <span className="text-sm font-semibold font-mono text-amber-900 tabular-nums" data-id="element-9809">
                  {fmt(item.lineTotal)}
                </span>
              </div>
            </motion.div>)}
        </div>

        {/* Dashed separator */}
        <div className="border-t-2 border-dashed border-amber-300 my-3" data-id="element-9810" />

        {/* Subtotal */}
        {showTotal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: lineItems.length * 0.08 + 0.1
      }} className="flex justify-between items-baseline mb-2" data-id="element-9811">
            <span className="text-sm font-bold text-amber-900" data-id="element-9812">Totaal</span>
            <span className="text-base font-bold font-mono text-amber-900 tabular-nums" data-id="element-9813">
              {fmt(subtotal)}
            </span>
          </motion.div>}

        {/* Budget & change */}
        {budgetVal > 0 && <>
            <div className="flex justify-between items-baseline text-sm text-amber-700 mb-1" data-id="element-9814">
              <span data-id="element-9815">Betaald</span>
              <span className="font-mono tabular-nums" data-id="element-9816">{fmt(budgetVal)}</span>
            </div>
            <div className={`flex justify-between items-baseline text-sm font-bold ${change >= 0 ? 'text-emerald-700' : 'text-red-600'}`} data-id="element-9817">
              <span data-id="element-9818">{change >= 0 ? 'Wisselgeld' : 'Tekort'}</span>
              <span className="font-mono tabular-nums" data-id="element-9819">
                {fmt(Math.abs(change))}
              </span>
            </div>
          </>}
      </div>

      {/* Educational tip */}
      <div className="bg-white/70 px-5 py-3 border-t border-amber-100" data-id="element-9820">
        <div className="flex items-start gap-2.5" data-id="element-9821">
          <span className="text-amber-500 text-sm mt-0.5" data-id="element-9822">💡</span>
          <p className="text-xs text-amber-800 leading-relaxed" data-id="element-9823">
            <strong data-id="element-9824">Tip:</strong> Tel de euro's en centen apart op. Komen de
            centen boven de 100? Dan gaat er 1 euro over.
          </p>
        </div>
      </div>
    </div>;
}